import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import article from "./StopLossContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";
import './PagesCss/StockProfitCalculator.css';

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const StopLossCalculator = () => {
  const content = article.find((item) => item.id === 1);
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    positionType: "long",
    entryPrice: "100.00",
    stopLossPrice: "95.00",
    takeProfitPrice: "110.00",
    quantity: "100",
    brokerage: "5.00",
    taxes: "2.00",
    leverage: "1",
    slippage: "0.50",
    financingCost: "0.00",
    trailingStop: "0",
    currencyConversion: "1",
    riskAmount: "100",
    portfolioSize: "10000",
    riskPerTrade: "2",
    volatility: "15",
    atrPeriod: "14",
    atrValue: "2.50"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const [riskLevel, setRiskLevel] = useState("medium");

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateStopLoss();
    }
  }, [inputs, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const calculateStopLoss = () => {
    const {
      positionType,
      entryPrice,
      stopLossPrice,
      takeProfitPrice,
      quantity,
      brokerage,
      taxes,
      leverage,
      slippage,
      financingCost,
      trailingStop,
      currencyConversion,
      riskAmount,
      portfolioSize,
      riskPerTrade,
      volatility,
      atrPeriod,
      atrValue
    } = inputs;

    if (!entryPrice || !stopLossPrice || !quantity) {
      return;
    }

    const entry = parseFloat(entryPrice);
    const stopLoss = parseFloat(stopLossPrice);
    const takeProfit = parseFloat(takeProfitPrice) || 0;
    const qty = parseFloat(quantity);
    const lev = parseFloat(leverage) || 1;
    const fee = parseFloat(brokerage || 0) + parseFloat(taxes || 0);
    const slip = parseFloat(slippage || 0);
    const finance = parseFloat(financingCost || 0);
    const trail = parseFloat(trailingStop || 0) / 100;
    const curr = parseFloat(currencyConversion || 1);
    const riskAmt = parseFloat(riskAmount || 0);
    const portfolio = parseFloat(portfolioSize || 0);
    const riskPercent = parseFloat(riskPerTrade || 0);

    // Calculate effective stop loss with slippage
    let effectiveStopLoss = stopLoss;
    if (positionType === "long") {
      effectiveStopLoss = stopLoss - slip;
      if (trail > 0) {
        effectiveStopLoss = Math.max(entry * (1 - trail), effectiveStopLoss);
      }
    } else {
      effectiveStopLoss = stopLoss + slip;
      if (trail > 0) {
        effectiveStopLoss = Math.min(entry * (1 + trail), effectiveStopLoss);
      }
    }

    // Calculate loss per share and total loss
    const lossPerShare = positionType === "long" 
      ? entry - effectiveStopLoss 
      : effectiveStopLoss - entry;

    const totalLoss = (lossPerShare * qty * lev) + fee + finance;
    const netTotalLoss = totalLoss * curr;

    // Calculate margin required
    const marginRequired = (entry * qty) / lev;

    // Risk percentage calculations
    const riskPercentage = (lossPerShare / entry) * 100;
    const positionRisk = (totalLoss / (entry * qty * lev)) * 100;

    // Risk-reward ratio
    let riskRewardRatio = 0;
    if (takeProfit > 0 && lossPerShare > 0) {
      const profitPerShare = positionType === "long" 
        ? takeProfit - entry 
        : entry - takeProfit;
      riskRewardRatio = profitPerShare / lossPerShare;
    }

    // Position sizing based on risk
    const calculatedPositionSize = riskAmt > 0 ? Math.floor(riskAmt / lossPerShare) : qty;
    const riskBasedPositionValue = calculatedPositionSize * entry;

    // ATR-based calculations
    const atr = parseFloat(atrValue || 0);
    const atrStopLoss = atr > 0 ? (positionType === "long" ? entry - (2 * atr) : entry + (2 * atr)) : 0;

    // Volatility-adjusted position size
    const volatilityAdjustment = parseFloat(volatility || 0) / 15; // Normalize to 15% volatility
    const adjustedPositionSize = Math.floor(calculatedPositionSize / (volatilityAdjustment || 1));

    setResult({
      totalLoss: netTotalLoss,
      lossPerShare: lossPerShare,
      marginRequired: marginRequired * curr,
      riskPercentage: Math.abs(riskPercentage),
      positionRisk: Math.abs(positionRisk),
      riskRewardRatio: riskRewardRatio.toFixed(2),
      calculatedPositionSize,
      riskBasedPositionValue: riskBasedPositionValue * curr,
      atrStopLoss,
      adjustedPositionSize,
      effectiveStopLoss,
      maxPositionSize: Math.floor((portfolio * (riskPercent / 100)) / lossPerShare),
      breakEvenPrice: positionType === "long" 
        ? entry + (fee / qty) 
        : entry - (fee / qty)
    });
  };

  const calculateRiskMetrics = () => {
    const { portfolioSize, riskPerTrade, volatility } = inputs;
    
    if (!portfolioSize || !riskPerTrade) return null;

    const portfolio = parseFloat(portfolioSize);
    const riskPercent = parseFloat(riskPerTrade);
    const vol = parseFloat(volatility || 15);

    const riskPerTradeAmount = portfolio * (riskPercent / 100);
    const dailyVar = portfolio * (vol / 100) / Math.sqrt(252);
    const maxDrawdown = portfolio * 0.1; // Conservative 10% max drawdown

    return {
      riskPerTradeAmount,
      dailyVar,
      maxDrawdown,
      suggestedPositionSize: riskPerTradeAmount / (portfolio * 0.02) // Based on 2% risk per trade
    };
  };

  const resetCalculator = () => {
    setInputs({
      positionType: "long",
      entryPrice: "100.00",
      stopLossPrice: "95.00",
      takeProfitPrice: "110.00",
      quantity: "100",
      brokerage: "5.00",
      taxes: "2.00",
      leverage: "1",
      slippage: "0.50",
      financingCost: "0.00",
      trailingStop: "0",
      currencyConversion: "1",
      riskAmount: "100",
      portfolioSize: "10000",
      riskPerTrade: "2",
      volatility: "15",
      atrPeriod: "14",
      atrValue: "2.50"
    });
    addNotification("Calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Total Potential Loss,${result.totalLoss.toFixed(2)}`,
      `Loss Per Share,${result.lossPerShare.toFixed(2)}`,
      `Risk Percentage,${result.riskPercentage.toFixed(2)}%`,
      `Risk/Reward Ratio,${result.riskRewardRatio}`,
      `Margin Required,${result.marginRequired.toFixed(2)}`,
      `Calculated Position Size,${result.calculatedPositionSize}`,
      `Effective Stop Loss,${result.effectiveStopLoss.toFixed(2)}`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stop-loss-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      result: { ...result }
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        riskPerTrade: "1", 
        leverage: "1", 
        trailingStop: "5" 
      },
      moderate: { 
        riskPerTrade: "2", 
        leverage: "2", 
        trailingStop: "8" 
      },
      aggressive: { 
        riskPerTrade: "3", 
        leverage: "3", 
        trailingStop: "12" 
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} risk preset applied`, "info");
  };

  // Chart Data
  const riskChartData = result ? {
    labels: ["Potential Loss", "Margin Used", "Risk Buffer", "Fees & Costs"],
    datasets: [{
      data: [
        Math.abs(result.totalLoss),
        result.marginRequired,
        Math.max(result.marginRequired - Math.abs(result.totalLoss), 0),
        parseFloat(inputs.brokerage) + parseFloat(inputs.taxes) + parseFloat(inputs.financingCost)
      ],
      backgroundColor: ["#FF6B6B", "#4ECDC4", "#45B7D1", "#FFA07A"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const riskMetrics = calculateRiskMetrics();

  return (
    <div className="profit-calculator">
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Stop-Loss & Risk Calculator</h1>
        </div>
        <p>Advanced position sizing, risk management, and stop-loss calculations</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.result.totalLoss >= 0 ? 'negative' : 'positive'}`}>
                        ${Math.abs(calc.result.totalLoss).toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Stop-Loss Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "sizing" ? "active" : ""}`}
                onClick={() => setActiveTab("sizing")}
              >
                <i className="fas fa-ruler"></i>
                {isMobile ? 'Sizing' : 'Position Sizing'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Risk Presets:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Trade Setup
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-up-down"></i> Position Type</label>
                      <select name="positionType" value={inputs.positionType} onChange={handleChange}>
                        <option value="long">Long</option>
                        <option value="short">Short</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Entry Price</label>
                      <input 
                        type="number" 
                        name="entryPrice" 
                        value={inputs.entryPrice} 
                        onChange={handleChange}
                        placeholder="100.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-down"></i> Stop-Loss Price</label>
                      <input 
                        type="number" 
                        name="stopLossPrice" 
                        value={inputs.stopLossPrice} 
                        onChange={handleChange}
                        placeholder="95.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrow-up"></i> Take Profit Price</label>
                      <input 
                        type="number" 
                        name="takeProfitPrice" 
                        value={inputs.takeProfitPrice} 
                        onChange={handleChange}
                        placeholder="110.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-layer-group"></i> Quantity</label>
                      <input 
                        type="number" 
                        name="quantity" 
                        value={inputs.quantity} 
                        onChange={handleChange}
                        placeholder="100"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-rocket"></i> Leverage</label>
                      <select name="leverage" value={inputs.leverage} onChange={handleChange}>
                        <option value="1">1x (No Leverage)</option>
                        <option value="2">2x</option>
                        <option value="3">3x</option>
                        <option value="5">5x</option>
                        <option value="10">10x</option>
                      </select>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-sliders-h"></i>
                    Cost & Risk Parameters
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Brokerage Fees</label>
                      <input 
                        type="number" 
                        name="brokerage" 
                        value={inputs.brokerage} 
                        onChange={handleChange}
                        placeholder="5.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Taxes</label>
                      <input 
                        type="number" 
                        name="taxes" 
                        value={inputs.taxes} 
                        onChange={handleChange}
                        placeholder="2.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Slippage</label>
                      <input 
                        type="number" 
                        name="slippage" 
                        value={inputs.slippage} 
                        onChange={handleChange}
                        placeholder="0.50"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-hand-holding-usd"></i> Financing Cost</label>
                      <input 
                        type="number" 
                        name="financingCost" 
                        value={inputs.financingCost} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-arrows-repeat"></i> Currency Rate</label>
                      <input 
                        type="number" 
                        name="currencyConversion" 
                        value={inputs.currencyConversion} 
                        onChange={handleChange}
                        placeholder="1.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-trailer"></i> Trailing Stop (%)</label>
                      <input 
                        type="number" 
                        name="trailingStop" 
                        value={inputs.trailingStop} 
                        onChange={handleChange}
                        placeholder="0"
                        step="0.1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Position Sizing Tab */}
          {activeTab === "sizing" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-ruler-combined"></i>
                  Advanced Position Sizing
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-exclamation-triangle"></i> Risk Amount ($)</label>
                    <input 
                      type="number" 
                      name="riskAmount" 
                      value={inputs.riskAmount} 
                      onChange={handleChange}
                      placeholder="100"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-piggy-bank"></i> Portfolio Size ($)</label>
                    <input 
                      type="number" 
                      name="portfolioSize" 
                      value={inputs.portfolioSize} 
                      onChange={handleChange}
                      placeholder="10000"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-percentage"></i> Risk Per Trade (%)</label>
                    <input 
                      type="number" 
                      name="riskPerTrade" 
                      value={inputs.riskPerTrade} 
                      onChange={handleChange}
                      placeholder="2"
                      step="0.1"
                    />
                  </div>
                </div><br/>

                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Volatility & ATR Settings
                </h3>
                <div className="input-row">
                  <div className="input-field">
                    <label><i className="fa-solid fa-wave-square"></i> Volatility (%)</label>
                    <input 
                      type="number" 
                      name="volatility" 
                      value={inputs.volatility} 
                      onChange={handleChange}
                      placeholder="15"
                      step="0.1"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-ruler"></i> ATR Period</label>
                    <input 
                      type="number" 
                      name="atrPeriod" 
                      value={inputs.atrPeriod} 
                      onChange={handleChange}
                      placeholder="14"
                    />
                  </div>
                  <div className="input-field">
                    <label><i className="fa-solid fa-ruler-horizontal"></i> ATR Value</label>
                    <input 
                      type="number" 
                      name="atrValue" 
                      value={inputs.atrValue} 
                      onChange={handleChange}
                      placeholder="2.50"
                      step="0.01"
                    />
                  </div>
                </div><br/>

                {riskMetrics && (
                  <div className="risk-results">
                    <h4>Portfolio Risk Metrics</h4>
                    <div className="risk-metrics">
                      <div className="risk-metric">
                        <span className="metric-label">Risk per Trade:</span>
                        <span className="metric-value">${riskMetrics.riskPerTradeAmount.toFixed(2)}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Daily VaR:</span>
                        <span className="metric-value">${riskMetrics.dailyVar.toFixed(2)}</span>
                      </div>
                      <div className="risk-metric">
                        <span className="metric-label">Max Drawdown:</span>
                        <span className="metric-value">${riskMetrics.maxDrawdown.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.result.totalLoss >= 0 ? 'negative' : 'positive'}`}>
                          <i className={`fas ${calc.result.totalLoss >= 0 ? 'fa-arrow-down' : 'fa-arrow-up'}`}></i>
                          ${Math.abs(calc.result.totalLoss).toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-percentage"></i> Risk: {calc.result.riskPercentage.toFixed(2)}%</span>
                        <span><i className="fas fa-balance-scale"></i> R/R: {calc.result.riskRewardRatio}</span>
                        <span><i className="fas fa-ruler"></i> Size: {calc.result.calculatedPositionSize}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "sizing") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-shield-alt"></i>
                    Risk Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className="result-value negative">
                          ${result.totalLoss.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-exclamation-triangle"></i>
                          Total Potential Loss
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.riskPercentage.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Risk Percentage
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.riskRewardRatio}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-balance-scale"></i>
                          Risk/Reward Ratio
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.calculatedPositionSize}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-ruler"></i>
                          Optimal Size
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <div className="detail-row">
                        <span><i className="fas fa-dollar-sign"></i> Loss Per Share:</span>
                        <span>${result.lossPerShare.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-hand-holding-usd"></i> Margin Required:</span>
                        <span>${result.marginRequired.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-ruler-horizontal"></i> Effective Stop Loss:</span>
                        <span>${result.effectiveStopLoss.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Position Risk:</span>
                        <span>{result.positionRisk.toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-target"></i> Break-even Price:</span>
                        <span>${result.breakEvenPrice.toFixed(2)}</span>
                      </div>
                      {result.atrStopLoss > 0 && (
                        <div className="detail-row">
                          <span><i className="fas fa-wave-square"></i> ATR-based Stop:</span>
                          <span>${result.atrStopLoss.toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    {/* Risk Assessment */}
                    <div className="risk-summary">
                      <h4>Position Assessment</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value">
                            {result.riskPercentage < 2 ? 'Low' : result.riskPercentage < 5 ? 'Medium' : 'High'}
                          </div>
                          <div className="risk-label">Risk Level</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {parseFloat(result.riskRewardRatio) >= 2 ? 'Good' : parseFloat(result.riskRewardRatio) >= 1 ? 'Fair' : 'Poor'}
                          </div>
                          <div className="risk-label">R/R Quality</div>
                        </div>
                      </div>
                    </div>

                    {/* Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Risk Distribution
                      </h3>
                      <Doughnut 
                        data={riskChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your trade details to see risk analysis</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "sizing" && result && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-ruler-combined"></i>
                  Position Sizing Results
                </h3>
                
                <div className="projection-summary">
                  <div className="summary-card">
                    <div className="summary-value">
                      {result.calculatedPositionSize}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-ruler"></i>
                      Risk-based Size
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {result.adjustedPositionSize}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-wave-square"></i>
                      Vol-adjusted Size
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      {result.maxPositionSize}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-chart-line"></i>
                      Portfolio-based Size
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">
                      ${result.riskBasedPositionValue.toFixed(2)}
                    </div>
                    <div className="summary-label">
                      <i className="fas fa-dollar-sign"></i>
                      Position Value
                    </div>
                  </div>
                </div><br/>

                <div className="risk-results-panel">
                  <h3>
                    <i className="fas fa-analytics"></i>
                    Sizing Recommendations
                  </h3>
                  <div className="risk-visualization">
                    <div className="position-breakdown">
                      <h4>Optimal Position Parameters</h4>
                      <div className="breakdown-item">
                        <span>Recommended Shares:</span>
                        <span>{Math.min(result.calculatedPositionSize, result.maxPositionSize)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Total Investment:</span>
                        <span>${(Math.min(result.calculatedPositionSize, result.maxPositionSize) * parseFloat(inputs.entryPrice)).toFixed(2)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Risk Amount:</span>
                        <span>${(Math.min(result.calculatedPositionSize, result.maxPositionSize) * result.lossPerShare).toFixed(2)}</span>
                      </div>
                      <div className="breakdown-item">
                        <span>Portfolio Risk:</span>
                        <span>{((Math.min(result.calculatedPositionSize, result.maxPositionSize) * result.lossPerShare) / parseFloat(inputs.portfolioSize) * 100).toFixed(2)}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default StopLossCalculator;